import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  cancelProductImageAuditJob,
  continueProductImageAuditJob,
  getActiveProductImageAuditJob,
  getProductImageAuditJob,
  listProductImageAuditIssues,
  startProductImageAuditJob,
} from "../../api/client";
import { getActiveWorkspaceSlug } from "../../utils/workspaceStorage";

const itemKey = (item) => `${item?.source || "gallery"}:${item?.imageId || 0}:${item?.productId || 0}`;

function formatBytes(bytes) {
  const n = Number(bytes) || 0;
  if (n <= 0) return "";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(n / 1000))} KB`;
}

function storageKey() {
  const slug = getActiveWorkspaceSlug() || "default";
  return `xpro:image-audit:${slug}`;
}

function readSavedJob() {
  try {
    const raw = sessionStorage.getItem(storageKey());
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return {
      scanJobId: String(parsed?.scanJobId || ""),
      fixJobId: String(parsed?.fixJobId || ""),
    };
  } catch {
    return null;
  }
}

function writeSavedJob(scanJobId, fixJobId) {
  try {
    if (!scanJobId && !fixJobId) sessionStorage.removeItem(storageKey());
    else sessionStorage.setItem(storageKey(), JSON.stringify({ scanJobId, fixJobId }));
  } catch {
    /* o navegador pode bloquear o storage */
  }
}

function reasonLabel(t, code) {
  if (code === "size") return t("imageAudit.reasonSize");
  if (code === "type") return t("imageAudit.reasonType");
  if (code === "edge") return t("imageAudit.reasonEdge");
  if (code === "unread") return t("imageAudit.reasonUnread");
  return code;
}

export default function ProductImageAuditDialog({ open, onClose, idsProjetos }) {
  const { t } = useTranslation("products");
  const [scanJobId, setScanJobId] = useState("");
  const [fixJobId, setFixJobId] = useState("");
  const [job, setJob] = useState(null);
  const [fixJob, setFixJob] = useState(null);
  const [items, setItems] = useState([]);
  const [totalIssues, setTotalIssues] = useState(0);
  const [selected, setSelected] = useState(() => new Set());
  const [error, setError] = useState("");
  const [pollError, setPollError] = useState("");
  const [notice, setNotice] = useState("");
  const [starting, setStarting] = useState(false);
  const [hydrating, setHydrating] = useState(false);
  const hydrated = useRef(false);

  const busyScan = job && (job.status === "queued" || job.status === "running");
  const busyFix = fixJob && (fixJob.status === "queued" || fixJob.status === "running");
  const busy = starting || busyScan || busyFix || hydrating;
  const fixDone = Number(fixJob?.queueIndex) || Number(fixJob?.fixed) || 0;

  useEffect(() => {
    if (!open || !hydrated.current) return undefined;
    writeSavedJob(scanJobId, fixJobId);
    return undefined;
  }, [open, scanJobId, fixJobId, hydrating]);

  useEffect(() => {
    if (!open) {
      hydrated.current = false;
      return undefined;
    }
    let stop = false;
    setHydrating(true);
    const attach = (active) => {
      const current = active?.job;
      if (!current?.jobId) return;
      if (current.mode === "fix") {
        setFixJobId(current.jobId);
        setFixJob(current);
        const scanId = current.scanJobId && current.scanJobId !== current.jobId
          ? current.scanJobId
          : active?.scan?.jobId || "";
        if (scanId) setScanJobId(scanId);
        if (active?.scan) setJob(active.scan);
      } else {
        setScanJobId(current.jobId);
        setJob(current);
        setFixJobId("");
        setFixJob(null);
      }
      if (current.status === "running" || current.status === "queued") {
        setNotice(t("imageAudit.resumed"));
      }
    };
    (async () => {
      try {
        const active = await getActiveProductImageAuditJob();
        if (stop) return;
        if (active?.active && active?.job?.jobId) {
          attach(active);
          return;
        }
        const saved = readSavedJob();
        if (saved?.fixJobId) setFixJobId(saved.fixJobId);
        if (saved?.scanJobId) setScanJobId(saved.scanJobId);
      } catch (err) {
        if (stop) return;
        const saved = readSavedJob();
        if (saved?.fixJobId) setFixJobId(saved.fixJobId);
        if (saved?.scanJobId) setScanJobId(saved.scanJobId);
        const missingEndpoint = err?.status === 404;
        if (!missingEndpoint) setPollError(err?.message || t("imageAudit.loadError"));
      } finally {
        if (!stop) {
          hydrated.current = true;
          setHydrating(false);
        }
      }
    })();
    return () => {
      stop = true;
    };
  }, [open, t]);

  useEffect(() => {
    if (!open || !scanJobId) return undefined;
    let stop = false;
    let timer = 0;
    const tick = async () => {
      try {
        const next = await getProductImageAuditJob(scanJobId);
        if (stop) return;
        setJob(next);
        setPollError("");
        const finished = next?.status === "completed" || next?.status === "failed" || next?.status === "cancelled";
        if (finished || next?.issueCount) {
          const page = await listProductImageAuditIssues(scanJobId, { offset: 0, limit: 100 });
          if (!stop) {
            setItems(page?.items || []);
            setTotalIssues(Number(page?.total) || 0);
          }
        }
        if (finished) {
          stop = true;
          clearInterval(timer);
        }
      } catch (err) {
        if (!stop) setPollError(err?.message || t("imageAudit.loadError"));
      }
    };
    tick();
    timer = setInterval(tick, 2500);
    return () => {
      stop = true;
      clearInterval(timer);
    };
  }, [open, scanJobId, t]);

  useEffect(() => {
    if (!open || !fixJobId) return undefined;
    let stop = false;
    let timer = 0;
    const tick = async () => {
      try {
        const next = await getProductImageAuditJob(fixJobId);
        if (stop) return;
        setFixJob(next);
        setPollError("");
        const finished = next?.status === "completed" || next?.status === "failed" || next?.status === "cancelled";
        if (scanJobId && !finished) {
          const page = await listProductImageAuditIssues(scanJobId, { offset: 0, limit: 100 });
          if (!stop) {
            setItems(page?.items || []);
            setTotalIssues(Number(page?.total) || 0);
          }
        }
        if (finished && scanJobId) {
          const page = await listProductImageAuditIssues(scanJobId, { offset: 0, limit: 100 });
          if (!stop) {
            setItems(page?.items || []);
            setTotalIssues(Number(page?.total) || 0);
          }
        }
        if (finished) {
          stop = true;
          clearInterval(timer);
        }
      } catch (err) {
        if (!stop) setPollError(err?.message || t("imageAudit.loadError"));
      }
    };
    tick();
    timer = setInterval(tick, 2500);
    return () => {
      stop = true;
      clearInterval(timer);
    };
  }, [open, fixJobId, scanJobId, t]);

  const pending = useMemo(
    () => items.filter((item) => item.status !== "done"),
    [items]
  );

  const startScan = async () => {
    setError("");
    setPollError("");
    setNotice("");
    setStarting(true);
    setItems([]);
    setSelected(new Set());
    setJob(null);
    setFixJob(null);
    setFixJobId("");
    setScanJobId("");
    try {
      const res = await startProductImageAuditJob({
        mode: "scan",
        ids_projetos: idsProjetos || "",
      });
      const id = res?.jobId || "";
      if (res?.alreadyRunning && res?.mode === "fix") {
        setFixJobId(id);
        setFixJob(res);
        const scanId = res?.scanJobId && res.scanJobId !== id ? res.scanJobId : "";
        setScanJobId(scanId);
        setNotice(t("imageAudit.resumed"));
      } else {
        setScanJobId(id);
        if (res?.alreadyRunning) setNotice(t("imageAudit.resumed"));
      }
    } catch (err) {
      setError(err?.message || t("imageAudit.startError"));
    } finally {
      setStarting(false);
    }
  };

  const applyFixStart = (res) => {
    if (!res?.jobId) return;
    if (res.alreadyRunning && res.mode !== "fix") {
      setScanJobId(res.jobId);
      setJob(res);
      setNotice(t("imageAudit.resumed"));
      return;
    }
    setFixJobId(res.jobId);
    setFixJob(res);
    if (res.scanJobId && res.scanJobId !== res.jobId) setScanJobId(res.scanJobId);
    if (res.alreadyRunning) setNotice(t("imageAudit.resumed"));
  };

  const startFixAll = async () => {
    if (!scanJobId) return;
    setError("");
    setStarting(true);
    try {
      const res = await startProductImageAuditJob({ mode: "fix", scanJobId });
      applyFixStart(res);
    } catch (err) {
      setError(err?.message || t("imageAudit.startError"));
    } finally {
      setStarting(false);
    }
  };
  const startFix = async (list) => {
    if (!scanJobId || !list?.length) return;
    setError("");
    setStarting(true);
    try {
      const res = await startProductImageAuditJob({
        mode: "fix",
        scanJobId,
        items: list,
      });
      applyFixStart(res);
    } catch (err) {
      setError(err?.message || t("imageAudit.startError"));
    } finally {
      setStarting(false);
    }
  };

  const resumeJob = async () => {
    const id = fixJobId || scanJobId;
    if (!id) return;
    setError("");
    try {
      const res = await continueProductImageAuditJob(id);
      setNotice(res?.busy ? t("imageAudit.busyPhoto") : t("imageAudit.resuming"));
    } catch (err) {
      setError(err?.message || t("imageAudit.continueError"));
    }
  };

  const openProduct = (productId) => {
    const url = new URL(window.location.href);
    url.searchParams.set("edit", String(productId));
    window.open(url.toString(), "_blank", "noopener,noreferrer");
  };

  const toggle = (key) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const selectedItems = items.filter((item) => selected.has(itemKey(item)) && item.status !== "done");
  const activityAt = busyFix ? fixJob?.updatedAt : job?.updatedAt;
  const stalled =
    (busyScan || busyFix) &&
    activityAt &&
    Date.now() - new Date(activityAt).getTime() > 90000;
  const failedStatus = fixJob?.status === "failed" || (!busyFix && job?.status === "failed");
  const serverMessage = (fixJob?.errorMessage || (!busyFix ? job?.errorMessage : "")) || "";
  const showResume = (stalled || failedStatus) && !serverMessage;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{t("imageAudit.title")}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {t("imageAudit.intro")}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
          {t("imageAudit.hint")}
        </Typography>

        {notice ? (
          <Alert severity="info" sx={{ mb: 2 }} onClose={() => setNotice("")}>
            {notice}
          </Alert>
        ) : null}

        {error ? (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        ) : null}

        {pollError ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {pollError}
          </Alert>
        ) : null}

        {serverMessage ? (
          <Alert
            severity={failedStatus ? "error" : "warning"}
            sx={{ mb: 2 }}
            action={
              <Button color="inherit" size="small" onClick={resumeJob}>
                {t("imageAudit.continue")}
              </Button>
            }
          >
            {serverMessage}
          </Alert>
        ) : null}

        {fixJob?.lastError && (busyFix || fixJob?.status === "failed") ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {t("imageAudit.lastFailed", { reason: fixJob.lastError })}
          </Alert>
        ) : null}

        {hydrating ? (
          <Box sx={{ mb: 2 }}>
            <LinearProgress />
          </Box>
        ) : null}

        {!scanJobId && !fixJobId && !hydrating ? (
          <Button variant="contained" onClick={startScan} disabled={starting} sx={{ textTransform: "none" }}>
            {t("imageAudit.check")}
          </Button>
        ) : null}

        {!busyFix && (job?.status === "completed" || job?.status === "failed" || job?.status === "cancelled") ? (
          <Button
            variant={items.length ? "text" : "contained"}
            onClick={() => {
              setScanJobId("");
              setJob(null);
              setItems([]);
              setTotalIssues(0);
              setSelected(new Set());
              setFixJob(null);
              setFixJobId("");
              setError("");
              setPollError("");
              setNotice("");
              writeSavedJob("", "");
            }}
            disabled={busy}
            sx={{ textTransform: "none", mb: 1 }}
          >
            {t("imageAudit.checkAgain")}
          </Button>
        ) : null}

        {busyScan && job?.mode !== "fix" ? (
          <Box sx={{ mb: 2 }}>
            <LinearProgress
              variant={job?.total ? "determinate" : "indeterminate"}
              value={job?.total ? Math.min(100, Math.round(((job.scanned || 0) / job.total) * 100)) : 0}
            />
            <Typography variant="body2" sx={{ mt: 1 }}>
              {t("imageAudit.checking", {
                done: job?.scanned || 0,
                total: job?.total || "…",
              })}
            </Typography>
          </Box>
        ) : null}

        {busyFix ? (
          <Box sx={{ mb: 2 }}>
            <LinearProgress
              variant={fixJob?.total ? "determinate" : "indeterminate"}
              value={fixJob?.total ? Math.min(100, Math.round((fixDone / fixJob.total) * 100)) : 0}
            />
            <Typography variant="body2" sx={{ mt: 1 }}>
              {t("imageAudit.fixing", {
                done: fixDone,
                total: fixJob?.total || "…",
              })}
            </Typography>
            {fixJob?.currentItem ? (
              <Typography variant="caption" display="block" color="text.secondary">
                {t("imageAudit.current", { name: fixJob.currentItem })}
              </Typography>
            ) : null}
            {fixJob?.failed ? (
              <Typography variant="caption" display="block" color="text.secondary">
                {t("imageAudit.failedSome", { count: fixJob.failed })}
              </Typography>
            ) : null}
            <Typography variant="caption" color="text.secondary">
              {t("imageAudit.canClose")}
            </Typography>
          </Box>
        ) : null}

        {showResume ? (
          <Alert
            severity={failedStatus ? "error" : "warning"}
            sx={{ mb: 2 }}
            action={
              <Button color="inherit" size="small" onClick={resumeJob}>
                {t("imageAudit.continue")}
              </Button>
            }
          >
            {failedStatus
              ? serverMessage || t("imageAudit.continueError")
              : busyFix
                ? t("imageAudit.stalledFix", {
                    done: fixDone,
                    total: fixJob?.total || "…",
                  })
                : t("imageAudit.stalled")}
          </Alert>
        ) : null}

        {job?.status === "completed" && !totalIssues ? (
          <Alert severity="success">{t("imageAudit.allGood")}</Alert>
        ) : null}

        {items.length ? (
          <Stack spacing={1} sx={{ mt: 1 }}>
            <Typography variant="body2">
              {t("imageAudit.found", { count: totalIssues })}
            </Typography>
            {items.map((item) => {
              const key = itemKey(item);
              const dims = item.width && item.height ? `${item.width}×${item.height}` : "";
              const size = formatBytes(item.bytes);
              return (
                <Box
                  key={key}
                  sx={{
                    display: "flex",
                    gap: 1,
                    alignItems: "flex-start",
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                    p: 1,
                  }}
                >
                  <Checkbox
                    size="small"
                    checked={selected.has(key)}
                    disabled={item.status === "done" || busy}
                    onChange={() => toggle(key)}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Link
                      component="button"
                      type="button"
                      underline="hover"
                      onClick={() => openProduct(item.productId)}
                      sx={{ fontWeight: 600, textAlign: "left" }}
                    >
                      {item.name || t("imageAudit.unnamed")}
                    </Link>
                    <Typography variant="caption" display="block" color="text.secondary">
                      {[item.sku ? `SKU ${item.sku}` : "", size, dims].filter(Boolean).join(" · ")}
                    </Typography>
                    <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: "wrap" }} useFlexGap>
                      {(item.reasons || []).map((code) => (
                        <Chip key={code} size="small" label={reasonLabel(t, code)} />
                      ))}
                      {item.status === "done" ? (
                        <Chip size="small" color="success" label={t("imageAudit.adjusted")} />
                      ) : null}
                      {item.status === "failed" ? (
                        <Chip size="small" color="error" label={t("imageAudit.failed")} />
                      ) : null}
                    </Stack>
                    {item.status === "failed" && item.error ? (
                      <Typography variant="caption" display="block" color="error" sx={{ mt: 0.5 }}>
                        {item.error}
                      </Typography>
                    ) : null}
                  </Box>
                  <Button
                    size="small"
                    disabled={busy || item.status === "done"}
                    onClick={() => startFix([item])}
                    sx={{ textTransform: "none" }}
                  >
                    {t("imageAudit.adjustOne")}
                  </Button>
                </Box>
              );
            })}
            {totalIssues > items.length ? (
              <Typography variant="caption" color="text.secondary">
                {t("imageAudit.more", { shown: items.length, total: totalIssues })}
              </Typography>
            ) : null}
          </Stack>
        ) : null}
      </DialogContent>
      <DialogActions>
        {busyScan || busyFix ? (
          <Button
            color="inherit"
            onClick={() => {
              const id = fixJobId || scanJobId;
              if (fixJobId) setFixJob((current) => ({ ...(current || {}), status: "cancelled" }));
              else setJob((current) => ({ ...(current || {}), status: "cancelled" }));
              cancelProductImageAuditJob(id).catch(() => {});
            }}
          >
            {t("imageAudit.cancel")}
          </Button>
        ) : null}
        <Button onClick={onClose} sx={{ textTransform: "none" }}>
          {t("imageAudit.close")}
        </Button>
        {pending.length ? (
          <>
            <Button
              disabled={busy || !selectedItems.length}
              onClick={() => startFix(selectedItems)}
              sx={{ textTransform: "none" }}
            >
              {t("imageAudit.adjustSelected")}
            </Button>
            <Button variant="contained" disabled={busy} onClick={startFixAll} sx={{ textTransform: "none" }}>
              {t("imageAudit.adjustAll")}
            </Button>
          </>
        ) : null}
      </DialogActions>
    </Dialog>
  );
}
